/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { Participants } from "../../../../redux-magic/sub-interfaces/chat-interface";
import { CurrentChatInterface } from "../../../../redux-magic/sub-interfaces/chat-states-interface";

/* Component Imports */

import { ObjectId } from "bson";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import MessageSection from "./MessageSection";
import { ChatSearchHeader, ChatSearchMatchBar, chatSearchMatchIds } from "./ChatSearch";
import { ChatForwardBar, ChatForwardDialog } from "./ChatForward";
import { Snackbar } from "@mui/material";
import { setChatMessagesInitialLoadDoneThunk } from "../../../../redux-magic/store";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "100%",
	background: theme.palette.background.paper,
}));

interface FileAndMore {
	file: File;
	mime_type: string;
}

const SingleChatScreen = ({
	session,
	dispatch,
	router,
	realtime_websocket,
	profile_context,
	current_chat_data,
	typing_chat_ids,
	desktop_fullscreen_mode,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	realtime_websocket: any;
	profile_context: any;
	current_chat_data: CurrentChatInterface;
	/* Conversations whose other side is typing (18 Oct 2026): the header reads "typing…". */
	typing_chat_ids?: Array<string>;
	desktop_fullscreen_mode: boolean;
}) => {
	/* Who "me" is (chat audit I5, 17 Oct 2026). The acting profile is the session's
	   profile_context, never `pages`: the web session trims `pages` and leaves the acting business
	   out of it, so `pages[0]` was always some other business or nothing. The party rule: a
	   business party is matched only by business_profile_id, a person only by user_id. A personal
	   profile_context's business_profile_id is a leftover id and is never read. */

	const loggedUserID: string | ObjectId = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
				: ""
			: ""
		: "";

	const acting_is_business_profile: boolean = profile_context?.is_business_profile === true;

	const acting_business_profile_id: string = acting_is_business_profile
		? String(profile_context?.business_profile_id || "")
		: "";

	const acting_user_id: string = String(loggedUserID || "");

	// An id that may arrive populated ({ _id, ... }), as an ObjectId or as a string.

	const idOf = (value: unknown): string =>
		value && typeof value === "object" && "_id" in value
			? String((value as { _id?: unknown })._id || "")
			: value
				? String(value)
				: "";

	const isActingParty = (
		party: { is_business_profile?: boolean | Boolean; user_id?: unknown; business_profile_id?: unknown } | null | undefined,
	): boolean => {
		if (!party) return false;
		// Some sender types use the Boolean wrapper; valueOf reads both shapes the same way.
		const party_is_business_profile: boolean = party.is_business_profile?.valueOf() === true;
		return acting_is_business_profile
			? party_is_business_profile &&
					acting_business_profile_id.length > 0 &&
					idOf(party.business_profile_id) === acting_business_profile_id
			: !party_is_business_profile && acting_user_id.length > 0 && idOf(party.user_id) === acting_user_id;
	};

	/* Private Chat Receiver: the first participant that is not the acting party */

	// `find`, not `splice(findIndex)`: with no match, splice(-1) took the LAST participant, which
	// could be the viewer (chat audit I12).
	const private_chat_receiver: Participants =
		(current_chat_data.participants || []).find((participant: Participants) => !isActingParty(participant)) ||
		(current_chat_data.participants || [])[0];

	/* The other side's name (a business's title, a person's name), for the reply strip. */
	const counterpart_name: string =
		private_chat_receiver?.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				private_chat_receiver.business_profile_id !== null &&
				"title" in (private_chat_receiver.business_profile_id as any)
				? String((private_chat_receiver.business_profile_id as any).title || "")
				: ""
			: typeof private_chat_receiver?.user_id === "object" && private_chat_receiver?.user_id !== null
				? [(private_chat_receiver.user_id as any).first_name, (private_chat_receiver.user_id as any).last_name]
						.filter(Boolean)
						.join(" ")
				: "";

	/* The other side's picture, the same one the header shows (ChatHeader), for their voice notes
	   (19 Oct 2026). A served file id is read through /profile-picture; an old one-chat-per-broadcast
	   thread carries a ready link. */
	const counterpart_picture_id: string =
		private_chat_receiver?.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				private_chat_receiver.business_profile_id !== null &&
				"display_picture_url" in (private_chat_receiver.business_profile_id as any)
				? String((private_chat_receiver.business_profile_id as any).display_picture_url?.file_id || "")
				: ""
			: typeof private_chat_receiver?.user_id === "object" &&
				  private_chat_receiver?.user_id !== null &&
				  "profile_picture" in (private_chat_receiver.user_id as any)
				? String((private_chat_receiver.user_id as any).profile_picture?.file_id || "")
				: "";
	const counterpart_image_url: string = !counterpart_picture_id
		? ""
		: counterpart_picture_id.includes("/images/icons/Avatar.svg")
			? "/images/icons/Avatar.svg"
			: current_chat_data.is_broadcast && current_chat_data.chat_source_collection !== "broadcast"
				? counterpart_picture_id
				: `/profile-picture/${counterpart_picture_id}`;

	const receiver_user_id: string | ObjectId =
		private_chat_receiver && private_chat_receiver.user_id
			? typeof private_chat_receiver.user_id === "object" && "_id" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id as { _id: string | ObjectId })._id
				: private_chat_receiver.user_id
			: "";

	const [selectedFilePreview, setSelectedFilePreview] = React.useState<Array<string>>([]);

	const [pendingUploads, setPendingUploads] = React.useState<Array<FileAndMore>>([]);

	const [current_message, setCurrentMessage] = React.useState<string>("");

	const [sendFiles, setSendFiles] = React.useState<boolean>(false);

	const [isBroadcastDeletedOrExpired, setIsBroadcastDeletedOrExpired] = React.useState<boolean>(false);

	/* Texts the composer has sent and the server hasn't stored yet (19 Oct 2026): the thread draws
	   each with the clock until it is stored. A new chat starts with none. */
	const [pendingTexts, setPendingTexts] = React.useState<Array<{ id: string; body: string; created_at: string }>>([]);
	React.useEffect(() => {
		setPendingTexts([]);
	}, [current_chat_data.chat_id]);
	const onPendingText = (change: { id: string; body: string } | { id: string; done: true }) => {
		if ("done" in change) {
			setPendingTexts((current) => current.filter((pending) => pending.id !== change.id));
		} else {
			setPendingTexts((current) => [...current, { id: change.id, body: change.body, created_at: new Date().toISOString() }]);
		}
	};

	/* Forwarding (18 Oct 2026, ChatForward.tsx): the picked messages (picking while non-empty), the
	   chat picker, and the line said once it is done. A new chat starts with nothing picked. */
	/* Search inside the conversation (gap A9, the app's): the query and the picked hit; null = closed. */
	const [searchQuery, setSearchQuery] = React.useState<string | null>(null);
	const [activeMatchId, setActiveMatchId] = React.useState<string>("");
	const searchOpen: boolean = searchQuery !== null;
	const matchIds: Array<string> = React.useMemo(
		() => (searchQuery ? chatSearchMatchIds(current_chat_data.messages || [], searchQuery) : []),
		[searchQuery, current_chat_data.messages],
	);
	const closeSearch = () => {
		setSearchQuery(null);
		setActiveMatchId("");
	};
	/* A new query parks on the newest hit, the one nearest the foot of the thread. */
	const changeSearchQuery = (query: string) => {
		setSearchQuery(query);
		const ids: Array<string> = chatSearchMatchIds(current_chat_data.messages || [], query);
		setActiveMatchId(ids.length > 0 ? ids[ids.length - 1] : "");
	};
	/* Both arrows wrap; an id no longer loaded reads as -1, so ⌄ lands on the oldest and ⌃ on the newest. */
	const stepMatch = (delta: number) => {
		if (matchIds.length === 0) return;
		const from: number = matchIds.indexOf(activeMatchId);
		setActiveMatchId(matchIds[(((from + delta) % matchIds.length) + matchIds.length) % matchIds.length]);
	};
	/* Another conversation opened: search closes, as leaving the app's screen does. */
	React.useEffect(() => {
		closeSearch();
	}, [current_chat_data.chat_id]);

	const [forwardSelection, setForwardSelection] = React.useState<Array<string>>([]);
	/* Picking messages to forward takes the composer's slot, so it ends a search. */
	React.useEffect(() => {
		if (forwardSelection.length > 0) closeSearch();
	}, [forwardSelection.length]);
	const [forwardDialogOpen, setForwardDialogOpen] = React.useState<boolean>(false);
	const [forwardNotice, setForwardNotice] = React.useState<string>("");
	React.useEffect(() => {
		setForwardSelection([]);
		setForwardDialogOpen(false);
	}, [current_chat_data.chat_id]);

	const forwardMessages = async (targetChatIds: Array<string>) => {
		// The picked messages in thread order.
		const ordered: Array<string> = (current_chat_data.messages || [])
			.filter((m: any) => forwardSelection.includes(String(m._id)))
			.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
			.map((m: any) => String(m._id));
		const ar: boolean = router.locale === "ar-AE";
		const kn: boolean = router.locale === "kn-IN";
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "chat/forward-messages", {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify({
					chat_id: current_chat_data.chat_id,
					message_ids: ordered,
					target_chat_ids: targetChatIds,
					is_business_profile: profile_context?.is_business_profile === true,
					user_id: session?.user?.dbUser?._id || profile_context?.user_id,
					business_profile_id: profile_context?.business_profile_id,
				}),
			});
			const result = await response.json().catch(() => null);
			if (!response.ok || result?.status !== "success") throw new Error("forward");
			const reached: Array<any> = result.payload?.forwarded || [];
			const skipped: number = (result.payload?.skipped || []).length;
			setForwardNotice(
				reached.length === 0
					? ar ? "تعذرت إعادة التوجيه إلى هذه المحادثات" : kn ? "ಆ ಚಾಟ್‌ಗಳಿಗೆ ಫಾರ್ವರ್ಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ" : "Couldn't forward to those chats"
					: skipped > 0
						? ar ? `تمت إعادة التوجيه إلى ${reached.length} من ${reached.length + skipped}` : kn ? `${reached.length + skipped} ರಲ್ಲಿ ${reached.length} ಚಾಟ್‌ಗಳಿಗೆ ಫಾರ್ವರ್ಡ್ ಮಾಡಲಾಗಿದೆ` : `Forwarded to ${reached.length} of ${reached.length + skipped} chats`
						: ar ? "تمت إعادة التوجيه" : kn ? "ಫಾರ್ವರ್ಡ್ ಮಾಡಲಾಗಿದೆ" : "Forwarded",
			);
			if (reached.length > 0) {
				setForwardDialogOpen(false);
				setForwardSelection([]);
				// Forwarded into this very chat: it re-reads its thread so the copies show.
				if (reached.some((r: any) => String(r.chat_id) === String(current_chat_data.chat_id) || (current_chat_data.chat_ids || []).includes(String(r.chat_id)))) {
					dispatch(setChatMessagesInitialLoadDoneThunk(false));
				}
			}
		} catch {
			setForwardNotice(ar ? "تعذرت إعادة التوجيه. يرجى المحاولة مرة أخرى." : kn ? "ಫಾರ್ವರ್ಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." : "Couldn't forward. Please try again.");
		}
	};

	/* Let's check if the acting profile sent the broadcast (chat audit I5: matched with the party rule above) */

	const latestMessage = current_chat_data.messages[current_chat_data.messages.length - 1];

	
	// Only a broadcast card names a broadcast: every message carries a `broadcast_id` (chat schema
	// 4.9, a random one on anything else), so the id alone says nothing.
	const broadcast_id: string | ObjectId =
		latestMessage && (latestMessage.is_broadcast === true || (latestMessage as any).message_type === "broadcast")
			? latestMessage.broadcast_id || ""
			: "";
	
	/* Let's check if the broadcast is deleted or expired */

	/* Handled properly (chat audit I12, 17 Oct 2026): a chat starts as open; only a clear answer that
	   the broadcast is deleted or expired closes it. A failed or unreadable check leaves the chat as
	   it is (it used to throw unhandled), and an answer for a broadcast the screen has moved on from
	   is dropped. */
	React.useEffect(() => {
		setIsBroadcastDeletedOrExpired(false);
		if (!broadcast_id) return;
		let cancelled: boolean = false;
		fetch(`${process.env.PRODUCTION_API_URL}chat/is-broadcast-deleted`, {
			method: "POST",
			cache: "no-store",
			body: JSON.stringify({
				broadcast_id: broadcast_id,
			}),
		})
			.then((res) => (res.ok ? res.json() : null))
			.then((data) => {
				if (cancelled || !data || data.status !== "success" || !data.payload) return;
				setIsBroadcastDeletedOrExpired(data.payload.is_broadcast_deleted === true || data.payload.is_broadcast_expired === true);
			})
			.catch(() => {
				// The check failed: the chat stays usable rather than closing on a guess.
			});
		return () => {
			cancelled = true;
		};
	}, [broadcast_id]);

	return (
		<RootDiv>
			{/* Chat Header */}
			{searchOpen ? (
				<ChatSearchHeader
					query={searchQuery || ""}
					onQueryChange={changeSearchQuery}
					onClose={closeSearch}
					locale={router.locale}
				/>
			) : null}
			{searchOpen ? null : <ChatHeader
				on_open_search={() => {
					setForwardSelection([]);
					setSearchQuery("");
					setActiveMatchId("");
				}}
				session={session}
				dispatch={dispatch}
				router={router}
				profile_context={profile_context}
				chat_id={current_chat_data.chat_id}
				chat_type={current_chat_data.chat_type}
				chat_title={current_chat_data.chat_title}
				chat_description={current_chat_data.chat_description}
				chat_is_broadcast={current_chat_data.is_broadcast}
				private_chat_receiver={private_chat_receiver}
				desktop_fullscreen_mode={desktop_fullscreen_mode}
				chat_source_collection={current_chat_data.chat_source_collection}
				chat_is_muted={current_chat_data.is_muted === true}
				my_block_id={current_chat_data.my_block_id || ""}
				chat_broadcast_id={current_chat_data.chat_broadcast_id ? String(current_chat_data.chat_broadcast_id) : ""}
				is_typing={(typing_chat_ids || []).some(
					(id) => id === String(current_chat_data.chat_id) || (current_chat_data.chat_ids || []).includes(id),
				)}
			/>}

			{/* Messages Section */}

			<MessageSection
				session={session}
				dispatch={dispatch}
				router={router}
				realtime_websocket={realtime_websocket}
				profile_context={profile_context}
				chat_id={current_chat_data.chat_id}
				messages={current_chat_data.messages}
				messages_count={current_chat_data.messages_count}
				messages_loading={current_chat_data.messages_loading}
				initial_load_done={current_chat_data.initial_load_done}
				filter_page_number={current_chat_data.filter_page_number}
				isBroadcast={current_chat_data.chat_source_collection === 'broadcast'}
				selected_files={selectedFilePreview}
				pending_uploads={pendingUploads}
				current_message={current_message}
				setCurrentMessage={setCurrentMessage}
				sendFiles={sendFiles}
				setSendFiles={setSendFiles}
				setSelectedFilePreview={setSelectedFilePreview}
				setPendingUploads={setPendingUploads}
				receiver_user_id={receiver_user_id}
				broadcast_id={current_chat_data.chat_broadcast_id}
				chat_last_message={current_chat_data.chat_last_message}
				has_unread_message={current_chat_data.has_unread_message}
				counterpart_name={counterpart_name}
				can_react={!current_chat_data.is_blocked && !current_chat_data.is_read_only && !current_chat_data.is_request}
				reply_to_message_id={current_chat_data.reply_to?.message_id || ""}
				unread_count={current_chat_data.unread_count || 0}
				blocked_by_me={Boolean(current_chat_data.my_block_id)}
				forward_selection={forwardSelection}
				on_forward_selection_change={setForwardSelection}
				counterpart_image_url={counterpart_image_url}
				pending_texts={pendingTexts}
				counterpart_party={
					private_chat_receiver
						? {
								is_business_profile: private_chat_receiver.is_business_profile === true,
								id: String(
									private_chat_receiver.is_business_profile === true
										? ((private_chat_receiver.business_profile_id as any)?._id ?? private_chat_receiver.business_profile_id ?? "")
										: ((private_chat_receiver.user_id as any)?._id ?? private_chat_receiver.user_id ?? ""),
								),
							}
						: null
				}
				search_query={searchQuery || ""}
				active_match_id={activeMatchId}
			/>

			{/* Input Container: while messages are picked to forward, the count and the forward button
			    take its place (18 Oct 2026). */}

			{forwardSelection.length > 0 ? (
				<ChatForwardBar
					count={forwardSelection.length}
					onCancel={() => setForwardSelection([])}
					onForward={() => setForwardDialogOpen(true)}
					locale={router.locale}
				/>
			) : null}
			<ChatForwardDialog
				open={forwardDialogOpen}
				onClose={() => setForwardDialogOpen(false)}
				onForward={forwardMessages}
				profile_context={profile_context}
				session={session}
				locale={router.locale}
			/>
			<Snackbar open={forwardNotice !== ""} autoHideDuration={3000} onClose={() => setForwardNotice("")} message={forwardNotice} />

			{searchOpen ? (
				<ChatSearchMatchBar
					matchNumber={matchIds.indexOf(activeMatchId) + 1}
					matchCount={matchIds.length}
					onPrevious={() => stepMatch(-1)}
					onNext={() => stepMatch(1)}
					locale={router.locale}
				/>
			) : null}
			{forwardSelection.length > 0 || searchOpen ? null : <ChatInput
				broadcast_id={current_chat_data.chat_broadcast_id}
				session={session}
				dispatch={dispatch}
				router={router}
				realtime_websocket={realtime_websocket}
				profile_context={profile_context}
				initial_load_done={current_chat_data.initial_load_done}
				messages_loading={current_chat_data.messages_loading}
				chat_id={current_chat_data.chat_id}
				property_id={current_chat_data.property_id}
				selectedFilePreview={selectedFilePreview}
				setSelectedFilePreview={setSelectedFilePreview}
				pendingUploads={pendingUploads}
				setPendingUploads={setPendingUploads}
				current_message={current_message}
				setCurrentMessage={setCurrentMessage}
				setSendFiles={setSendFiles}
				sendFiles={sendFiles}
				messages_count={current_chat_data.messages_count}
				isBroadcastDeletedOrExpired={isBroadcastDeletedOrExpired}
				receiver_user_id={receiver_user_id}
				chat_source_collection={current_chat_data.chat_source_collection}
				chat_dp={current_chat_data.chat_dp}
				current_chat_data={current_chat_data}
				on_pending_text={onPendingText}
			/>}
		</RootDiv>
	);
};

export default SingleChatScreen;
